import { useRouter } from 'next/router'
import ErrorPage from 'next/error'
import Image from 'next/image'
import { motion } from 'framer-motion'
import PageType from '@interfaces/page'
import Layout from '@comp/layout'
import Seo from '@comp/seo'
import FinalCta from '@comp/home/final-cta'
import ProseArticle from '@comp/content/prose-article'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import markdownToHtml from '@lib/markdownToHtml'
import { getPages, getPageBySlug } from '@lib/api'

const EASE = [0.22, 1, 0.36, 1] as const

type Props = {
  post: PageType
}

export default function FeaturePage({ post }: Props) {
  const router = useRouter()
  const title = `Monofor – ${post.title}`

  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />
  }

  return (
    <Layout>
      <Seo
        title={title}
        description={post.excerpt || `${post.title} — a Monofor platform feature.`}
        path={`/features/${post.slug}`}
        ogImage={post.coverImage}
      />
      <main className="bg-surface text-ink-primary">
        <section className="relative overflow-hidden bg-surface pt-20 pb-16 lg:pt-28 lg:pb-20">
          <BackgroundField />
          <Container size="lg" className="relative">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="brand">Feature</Eyebrow>
            </motion.div>
            <motion.div
              className="mt-6 max-w-3xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-xl">
                {post.title}
              </Heading>
            </motion.div>
            {post.excerpt && (
              <motion.div
                className="mt-6 max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: EASE, delay: 0.12 }}
              >
                <Text size="xl" tone="secondary">
                  {post.excerpt}
                </Text>
              </motion.div>
            )}
          </Container>
        </section>

        {post.coverImage && (
          <Container size="lg" className="-mt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.18 }}
              className="overflow-hidden rounded-3xl border border-line-subtle bg-surface-subtle"
            >
              <Image
                src={post.coverImage}
                alt={post.title}
                width={1200}
                height={630}
                priority
                sizes="(max-width: 1024px) 100vw, 1024px"
                className="h-auto w-full"
              />
            </motion.div>
          </Container>
        )}

        <article className="bg-surface pt-16 pb-24 lg:pt-20 lg:pb-32">
          <Container size="md">
            <ProseArticle html={post.content} />
          </Container>
        </article>

        <FinalCta />
      </main>
    </Layout>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-feature"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-feature)" />
      </svg>
    </div>
  )
}

type Params = {
  params: { slug: string }
}

export async function getStaticProps({ params }: Params) {
  const post = getPageBySlug('_pages/features', params.slug, [
    'title',
    'excerpt',
    'date',
    'slug',
    'content',
    'ogImage',
    'coverImage',
    'callToAction',
  ])

  if (!post) {
    return { props: {} }
  }

  const content = await markdownToHtml(post.content || '')

  return {
    props: {
      post: {
        ...post,
        content,
      },
    },
  }
}

export async function getStaticPaths() {
  const posts = getPages('_pages/features', ['slug'])
  const paths = posts
    .map((post) => ({ params: { slug: post.slug } }))
    .filter((x) => x.params.slug)
  return { paths, fallback: false }
}
